import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { getImageUrl, formatPrice } from '@/components/PropertyCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { BarChart3, Users, Building2, MessageSquare, MessageCircle, CheckCircle, XCircle, Clock, PlusCircle, Eye, Pencil, AlertTriangle, LayoutList, Search, Shield, ShieldCheck, BadgeCheck, Play, Loader2, Trophy, Check, Image as ImageIcon, History, Upload, KeyRound, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import UserAnalyticsTab from '@/components/UserAnalyticsTab';
import VerifiedBadge from '@/components/VerifiedBadge';
import StatesAdminTab from '@/components/StatesAdminTab';
import AuditLogTab from '@/components/AuditLogTab';
import VerificationDataCard from '@/components/VerificationDataCard';
import VerificationDiscrepancyBadge, { computeVerificationDiscrepancies } from '@/components/VerificationDiscrepancyBadge';
import { usePagedList, AdminPagination } from '@/components/admin/usePagedList';
import BetaAdminTab from '@/components/admin/BetaAdminTab';
import ServiceAgreementAdminTab from '@/components/admin/ServiceAgreementAdminTab';
import FeedbackAdminTab from '@/components/admin/FeedbackAdminTab';
import { Trash2, Sparkles, Gauge, Infinity as InfinityIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { t, language } = useLanguage();
  const { token, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingProperties, setPendingProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [allFilterStatus, setAllFilterStatus] = useState('');
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agencyForm, setAgencyForm] = useState({ name: '', description: '', phone: '', email: '', address: '', website: '', logo: '', cover_image: '', brand_color: '', tagline: '' });
  const [showAgencyDialog, setShowAgencyDialog] = useState(false);
  const [editingAgencyId, setEditingAgencyId] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  // Super Admin password reset dialog
  const [pwResetUser, setPwResetUser] = useState(null);
  const [pwResetValue, setPwResetValue] = useState('');
  const [pwResetSubmitting, setPwResetSubmitting] = useState(false);
  // Per-user listing limit override
  const [capUser, setCapUser] = useState(null);
  const [capDetail, setCapDetail] = useState(null); // GET response for the target user
  const [capValue, setCapValue] = useState('');
  const [capUnlimited, setCapUnlimited] = useState(false);
  const [capNote, setCapNote] = useState('');
  const [capSubmitting, setCapSubmitting] = useState(false);
  const [capLoading, setCapLoading] = useState(false);
  const [verifications, setVerifications] = useState([]);
  const [propertyVerifications, setPropertyVerifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Per-tab status filters (client-side except all-properties which uses server param `allFilterStatus`)
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [verifStatusFilter, setVerifStatusFilter] = useState('');
  const [pvStatusFilter, setPvStatusFilter] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  // searchQuery-filtered subset for the "All Properties" tab — must compute BEFORE the pagination hook
  const filteredAllProperties = useMemo(
    () => (allProperties || []).filter(p =>
      !searchQuery
      || p.title.toLowerCase().includes(searchQuery.toLowerCase())
      || (p.city || '').toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [allProperties, searchQuery]
  );

  // Pagination + filtering hooks (20/page)
  const pendingPaged = usePagedList(pendingProperties, '', () => '');
  const allPropsPaged = usePagedList(filteredAllProperties, '', () => '');
  const usersPaged = usePagedList(users, userRoleFilter, u => u.role || '');
  const agenciesPaged = usePagedList(agencies, '', () => '');
  const verifPaged = usePagedList(verifications, verifStatusFilter, v => v.status || '');
  const pvPaged = usePagedList(propertyVerifications, pvStatusFilter, v => v.status || '');

  const fetchData = async () => {
    try {
      const [statsRes, pendingRes, allRes, usersRes, agenciesRes, verifRes, pvRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/properties?status=pending`, { headers }),
        axios.get(`${API}/admin/properties?status=${allFilterStatus}`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/agencies`, { headers }),
        axios.get(`${API}/verification/admin/all`, { headers }).catch(() => ({ data: { verifications: [] } })),
        axios.get(`${API}/property-verification/admin/list`, { headers }).catch(() => ({ data: { verifications: [] } })),
      ]);
      setStats(statsRes.data);
      setPendingProperties(pendingRes.data.properties);
      setAllProperties(allRes.data.properties);
      setUsers(usersRes.data.users);
      setAgencies(agenciesRes.data.agencies);
      setVerifications(verifRes.data.verifications || []);
      setPropertyVerifications(pvRes.data.verifications || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [allFilterStatus]);

  const updatePropertyStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/properties/${id}/status`, { status }, { headers });
      toast.success(`Property ${status}`);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const deleteProperty = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta propiedad de forma permanente?' : 'Permanently delete this property?')) return;
    try {
      await axios.delete(`${API}/properties/${id}`, { headers });
      toast.success(language === 'es' ? 'Propiedad eliminada' : 'Property deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm((language === 'es' ? '¿Eliminar el usuario ' : 'Delete user ') + (u.email || u.name) + (language === 'es' ? '? Esta acción no se puede deshacer.' : '? This cannot be undone.'))) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { headers });
      toast.success(language === 'es' ? 'Usuario eliminado' : 'User deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteAgency = async (a) => {
    if (!window.confirm((language === 'es' ? '¿Eliminar la agencia ' : 'Delete agency ') + a.name + (language === 'es' ? '? Los agentes asociados quedarán como independientes.' : '? Linked agents will be detached.'))) return;
    try {
      await axios.delete(`${API}/admin/agencies/${a.id}`, { headers });
      toast.success(language === 'es' ? 'Agencia eliminada' : 'Agency deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateUserRole = async (userId, role, agencyId = '', agentTier = '') => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role, agency_id: agencyId, agent_tier: agentTier }, { headers });
      toast.success('Role updated');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const blankAgencyForm = { name: '', description: '', phone: '', email: '', address: '', website: '', logo: '', cover_image: '', brand_color: '', tagline: '' };

  const openCreateAgency = () => {
    setEditingAgencyId(null);
    setAgencyForm(blankAgencyForm);
    setShowAgencyDialog(true);
  };

  const openEditAgency = (agency) => {
    setEditingAgencyId(agency.id);
    setAgencyForm({
      name: agency.name || '',
      description: agency.description || '',
      phone: agency.phone || '',
      email: agency.email || '',
      address: agency.address || '',
      website: agency.website || '',
      logo: agency.logo || '',
      cover_image: agency.cover_image || '',
      brand_color: agency.brand_color || '',
      tagline: agency.tagline || '',
    });
    setShowAgencyDialog(true);
  };

  const saveAgency = async (e) => {
    e.preventDefault();
    try {
      if (editingAgencyId) {
        await axios.put(`${API}/agencies/${editingAgencyId}`, agencyForm, { headers });
        toast.success(language === 'es' ? 'Agencia actualizada' : 'Agency updated');
      } else {
        await axios.post(`${API}/agencies`, agencyForm, { headers });
        toast.success(language === 'es' ? 'Agencia creada' : 'Agency created');
      }
      setShowAgencyDialog(false);
      setEditingAgencyId(null);
      setAgencyForm(blankAgencyForm);
      fetchData();
    } catch { toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save'); }
  };

  const uploadAgencyAsset = async (file, field) => {
    if (!file) return;
    const setBusy = field === 'logo' ? setUploadingLogo : setUploadingCover;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setAgencyForm(f => ({ ...f, [field]: fullUrl }));
      toast.success(language === 'es' ? 'Imagen subida' : 'Image uploaded');
    } catch {
      toast.error(language === 'es' ? 'Error al subir' : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const openPwResetDialog = (u) => {
    setPwResetUser(u);
    setPwResetValue('');
  };

  const submitPasswordReset = async () => {
    if (!pwResetUser) return;
    if (!pwResetValue || pwResetValue.length < 6) {
      toast.error(language === 'es' ? 'Mínimo 6 caracteres' : 'Min 6 characters');
      return;
    }
    setPwResetSubmitting(true);
    try {
      await axios.put(`${API}/admin/users/${pwResetUser.id}/password`,
        { new_password: pwResetValue, notify_user: true }, { headers });
      toast.success(language === 'es' ? 'Contraseña restablecida' : 'Password reset');
      setPwResetUser(null);
      setPwResetValue('');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'es' ? 'Error' : 'Failed'));
    } finally {
      setPwResetSubmitting(false);
    }
  };

  const openCapDialog = async (u) => {
    setCapUser(u);
    setCapDetail(null);
    setCapValue('');
    setCapUnlimited(false);
    setCapNote('');
    setCapLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/${u.id}/listing-limit`, { headers });
      setCapDetail(r.data);
      const ov = r.data.override;
      if (ov === 'unlimited') {
        setCapUnlimited(true);
        setCapValue('');
      } else if (typeof ov === 'number') {
        setCapValue(String(ov));
        setCapUnlimited(false);
      } else {
        setCapValue('');
        setCapUnlimited(false);
      }
      setCapNote(r.data.override_note || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'es' ? 'Error al cargar' : 'Failed to load'));
      setCapUser(null);
    } finally {
      setCapLoading(false);
    }
  };

  const submitCap = async (mode) => {
    if (!capUser) return;
    let body;
    if (mode === 'clear') {
      body = { override: null, note: '' };
    } else if (capUnlimited) {
      body = { override: 'unlimited', note: capNote.trim() };
    } else if (capValue.trim() === '') {
      body = { override: null, note: '' };
    } else {
      const n = parseInt(capValue.trim(), 10);
      if (!Number.isFinite(n) || n < 0) {
        toast.error(language === 'es' ? 'Debe ser un entero >= 0' : 'Must be a non-negative integer');
        return;
      }
      body = { override: n, note: capNote.trim() };
    }
    setCapSubmitting(true);
    try {
      const r = await axios.patch(`${API}/admin/users/${capUser.id}/listing-limit`, body, { headers });
      toast.success(r.data.message || (language === 'es' ? 'Guardado' : 'Saved'));
      setCapUser(null);
      // Refresh users list so the badge updates
      const u = await axios.get(`${API}/admin/users`, { headers });
      setUsers(u.data.users || u.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'es' ? 'Error' : 'Failed'));
    } finally {
      setCapSubmitting(false);
    }
  };

  const statCards = stats ? [
    { label: t('admin.totalUsers'), value: stats.total_users, icon: Users, color: 'text-blue-600 bg-blue-50' },
    { label: t('admin.totalProperties'), value: stats.total_properties, icon: Building2, color: 'text-primary bg-teal-50' },
    { label: t('admin.pendingProperties'), value: stats.pending_properties, icon: Clock, color: 'text-yellow-600 bg-yellow-50' },
    { label: t('admin.approvedProperties'), value: stats.approved_properties, icon: CheckCircle, color: 'text-green-600 bg-green-50' },
    { label: t('admin.totalAgencies'), value: stats.total_agencies, icon: Building2, color: 'text-purple-600 bg-purple-50' },
    { label: t('admin.totalContacts'), value: stats.total_contacts, icon: MessageSquare, color: 'text-accent bg-orange-50' },
  ] : [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">{t('admin.title')}</h1>
        <Link to="/admin/top-agents" data-testid="link-top-agents">
          <Button className="gap-1.5 bg-[#0B192C] hover:bg-[#1B2A4E] text-white h-9">
            <Trophy className="w-4 h-4 text-[#D4AF37]" />
            {language === 'es' ? 'Agentes Mejor Valorados' : 'Top-Rated Agents'}
          </Button>
        </Link>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats">
          {statCards.map((s, i) => (
            <Card key={i}>
              <CardContent className="p-3 flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.color}`}><s.icon className="w-4 h-4" /></div>
                <div><div className="font-heading text-lg font-bold">{s.value}</div><div className="text-[11px] text-slate-500 leading-tight">{s.label}</div></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="bg-slate-100 p-1 rounded-xl" data-testid="admin-tabs">
          <TabsTrigger value="pending" className="rounded-lg gap-1.5" data-testid="admin-tab-pending">
            <AlertTriangle className="w-4 h-4" />
            {language === 'es' ? 'Pendientes' : 'Pending Approval'}
            {pendingProperties.length > 0 && <Badge className="ml-1 bg-yellow-500 text-white text-xs h-5 px-1.5">{pendingProperties.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="all-properties" className="rounded-lg gap-1.5" data-testid="admin-tab-all">
            <LayoutList className="w-4 h-4" />{language === 'es' ? 'Todas las Propiedades' : 'All Properties'}
          </TabsTrigger>
          <TabsTrigger value="users" className="rounded-lg" data-testid="admin-tab-users">{t('admin.users')}</TabsTrigger>
          <TabsTrigger value="agencies" className="rounded-lg" data-testid="admin-tab-agencies">{t('admin.agencies')}</TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-lg gap-1.5" data-testid="admin-tab-analytics">
            <BarChart3 className="w-4 h-4" />{language === 'es' ? 'Analisis de Usuarios' : 'User Analytics'}
          </TabsTrigger>
          <TabsTrigger value="verifications" className="rounded-lg gap-1.5" data-testid="admin-tab-verifications">
            <BadgeCheck className="w-4 h-4" />{language === 'es' ? 'Verificaciones' : 'Verifications'}
            {verifications.filter(v => v.status === 'under_review').length > 0 && (
              <Badge className="ml-1 bg-amber-500 text-white text-xs h-5 px-1.5">{verifications.filter(v => v.status === 'under_review').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="property-verifications" className="rounded-lg gap-1.5" data-testid="admin-tab-property-verifications">
            <ShieldCheck className="w-4 h-4" />{language === 'es' ? 'Verif. Propiedades' : 'Property Verifs.'}
            {propertyVerifications.filter(v => v.status === 'under_review').length > 0 && (
              <Badge className="ml-1 bg-amber-500 text-white text-xs h-5 px-1.5">{propertyVerifications.filter(v => v.status === 'under_review').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="states" className="rounded-lg gap-1.5" data-testid="admin-tab-states">
            <ImageIcon className="w-4 h-4" />{language === 'es' ? 'Estados' : 'States'}
          </TabsTrigger>
          {currentUser?.role === 'superadmin' && (
            <TabsTrigger value="beta" className="rounded-lg gap-1.5" data-testid="admin-tab-beta">
              <Sparkles className="w-4 h-4" />Beta
            </TabsTrigger>
          )}
          {currentUser?.role === 'superadmin' && (
            <TabsTrigger value="service-agreement" className="rounded-lg gap-1.5" data-testid="admin-tab-service-agreement">
              <FileText className="w-4 h-4" />{language === 'es' ? 'Acuerdo' : 'Agreement'}
            </TabsTrigger>
          )}
          {currentUser?.role === 'superadmin' && (
            <TabsTrigger value="feedback" className="rounded-lg gap-1.5" data-testid="admin-tab-feedback">
              <MessageCircle className="w-4 h-4" />{language === 'es' ? 'Comentarios' : 'Feedback'}
            </TabsTrigger>
          )}
          {currentUser?.role === 'superadmin' && (
            <TabsTrigger value="audit-log" className="rounded-lg gap-1.5" data-testid="admin-tab-audit-log">
              <History className="w-4 h-4" />{language === 'es' ? 'Auditoría' : 'Audit Log'}
            </TabsTrigger>
          )}
        </TabsList>

        {/* ===== PENDING APPROVAL TAB ===== */}
        <TabsContent value="pending">
          {pendingProperties.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-border" data-testid="no-pending">
              <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">{language === 'es' ? 'No hay propiedades pendientes de aprobacion' : 'No properties pending approval'}</p>
              <p className="text-xs text-slate-400 mt-1">{language === 'es' ? 'Todas las propiedades han sido revisadas' : 'All properties have been reviewed'}</p>
            </div>
          ) : (
            <>
            <div className="space-y-3">
              {pendingPaged.paged.map(prop => (
                <div key={prop.id} className="bg-white rounded-xl border-2 border-yellow-200 p-4 flex items-center gap-4" data-testid={`pending-property-${prop.id}`}>
                  {/* Thumbnail */}
                  <div className="w-28 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 hidden sm:block">
                    <img src={getImageUrl(prop.main_photo)} alt={prop.title} className="w-full h-full object-cover" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate">{prop.title}</h3>
                    <p className="text-xs text-slate-500">{prop.city}{prop.neighborhood ? `, ${prop.neighborhood}` : ''}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="font-heading font-bold text-primary text-sm">{formatPrice(prop.price, prop.currency, prop.operation_type)}</span>
                      <Badge variant="outline" className="text-xs capitalize">{t(`types.${prop.property_type}`)}</Badge>
                      <Badge variant="outline" className="text-xs capitalize">{t(`operations.${prop.operation_type}`)}</Badge>
                      <span className="text-xs text-slate-400">{prop.area} m²</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-1">{prop.description}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" className="h-8 gap-1"
                      onClick={() => navigate(`/property/${prop.id}`)} data-testid={`view-pending-${prop.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 gap-1"
                      onClick={() => navigate(`/property/${prop.id}/edit`)} data-testid={`edit-pending-${prop.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" className="h-8 gap-1 bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => updatePropertyStatus(prop.id, 'approved')} data-testid={`approve-${prop.id}`}>
                      <CheckCircle className="w-3.5 h-3.5" />{t('admin.approve')}
                    </Button>
                    <Button size="sm" variant="destructive" className="h-8 gap-1"
                      onClick={() => updatePropertyStatus(prop.id, 'rejected')} data-testid={`reject-${prop.id}`}>
                      <XCircle className="w-3.5 h-3.5" />{t('admin.reject')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <AdminPagination
              page={pendingPaged.page}
              totalPages={pendingPaged.totalPages}
              setPage={pendingPaged.setPage}
              totalItems={pendingPaged.filtered.length}
              testIdPrefix="pending-pagination"
            />
            </>
          )}
        </TabsContent>

        {/* ===== ALL PROPERTIES TAB ===== */}
        <TabsContent value="all-properties">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder={language === 'es' ? 'Buscar por titulo o ciudad...' : 'Search by title or city...'}
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 h-9 bg-slate-50" data-testid="admin-search-properties" />
            </div>
            <div className="flex gap-2">
              {[
                { val: '', label: language === 'es' ? 'Todas' : 'All' },
                { val: 'approved', label: t('property.approved') },
                { val: 'pending', label: t('property.pending') },
                { val: 'rejected', label: t('property.rejected') },
              ].map(s => (
                <Button key={s.val} variant={allFilterStatus === s.val ? 'default' : 'outline'} size="sm"
                  className={allFilterStatus === s.val ? 'bg-primary text-white' : ''}
                  onClick={() => setAllFilterStatus(s.val)} data-testid={`admin-filter-${s.val || 'all'}`}>
                  {s.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>{language === 'es' ? 'Propiedad' : 'Property'}</TableHead>
                  <TableHead>{t('search.city')}</TableHead>
                  <TableHead>{t('property.price')}</TableHead>
                  <TableHead>{language === 'es' ? 'Tipo' : 'Type'}</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>{t('property.views')}</TableHead>
                  <TableHead>{language === 'es' ? 'Acciones' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allPropsPaged.paged.map(prop => (
                  <TableRow key={prop.id} data-testid={`admin-property-${prop.id}`}>
                    <TableCell>
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100">
                        <img src={getImageUrl(prop.main_photo)} alt="" className="w-full h-full object-cover" />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium max-w-[180px] truncate">{prop.title}</TableCell>
                    <TableCell className="text-sm">{prop.city}</TableCell>
                    <TableCell className="font-semibold text-sm">{formatPrice(prop.price, prop.currency, prop.operation_type)}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize text-xs">{t(`types.${prop.property_type}`)}</Badge></TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${prop.status === 'approved' ? 'bg-green-100 text-green-700' : prop.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                        {t(`property.${prop.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell><span className="flex items-center gap-1 text-sm text-slate-500"><Eye className="w-3.5 h-3.5" />{prop.views_count}</span></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                          onClick={() => navigate(`/property/${prop.id}`)} data-testid={`admin-view-${prop.id}`}>
                          <Eye className="w-3.5 h-3.5 text-slate-500" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                          onClick={() => navigate(`/property/${prop.id}/edit`)} data-testid={`admin-edit-${prop.id}`}>
                          <Pencil className="w-3.5 h-3.5 text-slate-500" />
                        </Button>
                        {prop.status !== 'approved' && (
                          <Button size="sm" variant="ghost" className="h-7 text-green-600 hover:bg-green-50 px-1.5"
                            onClick={() => updatePropertyStatus(prop.id, 'approved')}>
                            <CheckCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {prop.status !== 'rejected' && (
                          <Button size="sm" variant="ghost" className="h-7 text-red-600 hover:bg-red-50 px-1.5"
                            onClick={() => updatePropertyStatus(prop.id, 'rejected')}>
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {currentUser?.role === 'superadmin' && (
                          <Button size="sm" variant="ghost" className="h-7 text-red-700 hover:bg-red-50 px-1.5"
                            onClick={() => deleteProperty(prop.id)} data-testid={`admin-delete-property-${prop.id}`}
                            title={language === 'es' ? 'Eliminar' : 'Delete'}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredAllProperties.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">
                    {language === 'es' ? 'No se encontraron propiedades' : 'No properties found'}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <AdminPagination page={allPropsPaged.page} totalPages={allPropsPaged.totalPages}
            setPage={allPropsPaged.setPage} totalItems={allPropsPaged.filtered.length}
            testIdPrefix="all-props-pagination" />
        </TabsContent>

        {/* ===== USERS TAB ===== */}
        <TabsContent value="users">
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { val: '', label: language === 'es' ? 'Todos' : 'All' },
              { val: 'user', label: language === 'es' ? 'Usuarios' : 'Users' },
              { val: 'agent', label: language === 'es' ? 'Agentes' : 'Agents' },
              { val: 'lawyer', label: language === 'es' ? 'Abogados' : 'Lawyers' },
              { val: 'admin', label: 'Admins' },
              { val: 'superadmin', label: 'Super Admin' },
            ].map(s => (
              <Button key={s.val} variant={userRoleFilter === s.val ? 'default' : 'outline'} size="sm"
                className={userRoleFilter === s.val ? 'bg-primary text-white' : ''}
                onClick={() => setUserRoleFilter(s.val)} data-testid={`users-filter-${s.val || 'all'}`}>
                {s.label}
              </Button>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{language === 'es' ? 'Nombre' : 'Name'}</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>{language === 'es' ? 'Rol' : 'Role'}</TableHead>
                  <TableHead>{language === 'es' ? 'Tipo Agente' : 'Agent Tier'}</TableHead>
                  <TableHead>{language === 'es' ? 'Agencia' : 'Agency'}</TableHead>
                  <TableHead>{language === 'es' ? 'Cambiar Rol' : 'Change Role'}</TableHead>
                  <TableHead>{language === 'es' ? 'Tier' : 'Tier'}</TableHead>
                  <TableHead>{language === 'es' ? 'Asignar Agencia' : 'Assign Agency'}</TableHead>
                  {currentUser?.role === 'superadmin' && (
                    <TableHead className="text-right">{language === 'es' ? 'Acciones' : 'Actions'}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {usersPaged.paged.map(u => (
                  <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{u.role}</Badge></TableCell>
                    <TableCell>
                      {u.role === 'agent' ? (
                        <Badge className={u.agent_tier === 'pro' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                          {u.agent_tier === 'pro' ? 'Pro' : 'Independent'}
                        </Badge>
                      ) : <span className="text-xs text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-sm">{agencies.find(a => a.id === u.agency_id)?.name || '-'}</TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(role) => updateUserRole(u.id, role, u.agency_id, u.agent_tier)}>
                        <SelectTrigger className="h-8 w-32" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="agent">Agent</SelectItem>
                          <SelectItem value="lawyer">Lawyer</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          {currentUser?.role === 'superadmin' && (
                            <SelectItem value="superadmin" data-testid={`role-option-superadmin-${u.id}`}>Super Admin</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {u.role === 'agent' ? (
                        <Select value={u.agent_tier || 'independent'} onValueChange={(tier) => updateUserRole(u.id, u.role, u.agency_id, tier)}>
                          <SelectTrigger className="h-8 w-32" data-testid={`tier-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="independent">Independent</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : <span className="text-xs text-slate-400">-</span>}
                    </TableCell>
                    <TableCell>
                      <Select value={u.agency_id || 'none'} onValueChange={(aid) => updateUserRole(u.id, u.role, aid === 'none' ? '' : aid, u.agent_tier)}>
                        <SelectTrigger className="h-8 w-40" data-testid={`agency-select-${u.id}`}><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{language === 'es' ? 'Ninguna' : 'None'}</SelectItem>
                          {agencies.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    {currentUser?.role === 'superadmin' && (
                      <TableCell className="text-right">
                        {u.agency_id ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => openPwResetDialog(u)}
                            data-testid={`reset-password-${u.id}`}
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                            {language === 'es' ? 'Restablecer' : 'Reset Pwd'}
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                        {(u.role === 'agent' || u.role === 'user') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className={`h-8 gap-1 ml-1 ${
                              u.listing_limit_override !== undefined && u.listing_limit_override !== null
                                ? 'border-[#D4AF37] bg-amber-50 text-[#0B192C] hover:bg-amber-100'
                                : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                            }`}
                            onClick={() => openCapDialog(u)}
                            data-testid={`cap-override-${u.id}`}
                            title={language === 'es' ? 'Límite de propiedades' : 'Listing limit'}
                          >
                            {u.listing_limit_override === 'unlimited' ? (
                              <><InfinityIcon className="w-3.5 h-3.5" />∞</>
                            ) : typeof u.listing_limit_override === 'number' ? (
                              <><Gauge className="w-3.5 h-3.5" />{u.listing_limit_override}</>
                            ) : (
                              <><Gauge className="w-3.5 h-3.5" />{language === 'es' ? 'Límite' : 'Cap'}</>
                            )}
                          </Button>
                        )}
                        {u.id !== currentUser?.id && (
                          <Button
                            size="sm" variant="outline"
                            className="h-8 gap-1 border-red-300 text-red-700 hover:bg-red-50 ml-1"
                            onClick={() => deleteUser(u)}
                            data-testid={`admin-delete-user-${u.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <AdminPagination page={usersPaged.page} totalPages={usersPaged.totalPages}
            setPage={usersPaged.setPage} totalItems={usersPaged.filtered.length}
            testIdPrefix="users-pagination" />
        </TabsContent>

        {/* ===== AGENCIES TAB ===== */}
        <TabsContent value="agencies">
          <div className="flex justify-end mb-4">
            <Dialog open={showAgencyDialog} onOpenChange={(o) => { setShowAgencyDialog(o); if (!o) { setEditingAgencyId(null); setAgencyForm(blankAgencyForm); } }}>
              <DialogTrigger asChild>
                <Button onClick={openCreateAgency} className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1.5" data-testid="create-agency-btn">
                  <PlusCircle className="w-4 h-4" />{t('admin.createAgency')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingAgencyId
                      ? (language === 'es' ? 'Editar Agencia' : 'Edit Agency')
                      : t('admin.createAgency')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={saveAgency} className="space-y-3">
                  <div><Label>{t('admin.agencyName')}</Label><Input value={agencyForm.name} onChange={e => setAgencyForm(f => ({...f, name: e.target.value}))} required className="mt-1" data-testid="agency-name-input" /></div>
                  <div><Label>{t('admin.agencyEmail')}</Label><Input value={agencyForm.email} onChange={e => setAgencyForm(f => ({...f, email: e.target.value}))} className="mt-1" data-testid="agency-email-input" /></div>
                  <div><Label>{t('admin.agencyPhone')}</Label><Input value={agencyForm.phone} onChange={e => setAgencyForm(f => ({...f, phone: e.target.value}))} className="mt-1" /></div>
                  <div><Label>{t('admin.agencyDescription')}</Label><Textarea value={agencyForm.description} onChange={e => setAgencyForm(f => ({...f, description: e.target.value}))} className="mt-1" /></div>
                  <div><Label>{t('admin.agencyAddress')}</Label><Input value={agencyForm.address} onChange={e => setAgencyForm(f => ({...f, address: e.target.value}))} className="mt-1" /></div>
                  <div><Label>{t('admin.agencyWebsite')}</Label><Input value={agencyForm.website} onChange={e => setAgencyForm(f => ({...f, website: e.target.value}))} className="mt-1" /></div>

                  {/* ===== Branding section ===== */}
                  <div className="pt-3 mt-2 border-t border-border">
                    <h4 className="font-heading text-sm font-semibold mb-2 flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#D4AF37]" />{language === 'es' ? 'Marca de la Agencia' : 'Agency Branding'}</h4>

                    {/* Logo */}
                    <div className="mb-3">
                      <Label>{language === 'es' ? 'Logo' : 'Logo'}</Label>
                      <div className="flex items-center gap-3 mt-1">
                        {agencyForm.logo ? (
                          <img src={agencyForm.logo} alt="logo" className="h-14 w-14 rounded-lg object-cover border" data-testid="agency-logo-preview" />
                        ) : (
                          <div className="h-14 w-14 rounded-lg bg-slate-100 flex items-center justify-center border"><Building2 className="w-6 h-6 text-slate-400" /></div>
                        )}
                        <div className="flex-1 flex flex-col gap-1.5">
                          <Input value={agencyForm.logo} onChange={e => setAgencyForm(f => ({...f, logo: e.target.value}))} placeholder="https://... or upload" data-testid="agency-logo-url-input" />
                          <label className="inline-flex items-center gap-1.5 text-xs text-primary cursor-pointer hover:underline">
                            {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            {uploadingLogo ? (language === 'es' ? 'Subiendo...' : 'Uploading...') : (language === 'es' ? 'Subir logo' : 'Upload logo')}
                            <input type="file" accept="image/*" className="hidden" data-testid="agency-logo-upload" onChange={e => uploadAgencyAsset(e.target.files?.[0], 'logo')} />
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Cover image */}
                    <div className="mb-3">
                      <Label>{language === 'es' ? 'Imagen de Portada' : 'Cover Image'}</Label>
                      <div className="flex items-center gap-3 mt-1">
                        {agencyForm.cover_image ? (
                          <img src={agencyForm.cover_image} alt="cover" className="h-14 w-24 rounded-lg object-cover border" data-testid="agency-cover-preview" />
                        ) : (
                          <div className="h-14 w-24 rounded-lg bg-slate-100 flex items-center justify-center border"><ImageIcon className="w-6 h-6 text-slate-400" /></div>
                        )}
                        <div className="flex-1 flex flex-col gap-1.5">
                          <Input value={agencyForm.cover_image} onChange={e => setAgencyForm(f => ({...f, cover_image: e.target.value}))} placeholder="https://... or upload" data-testid="agency-cover-url-input" />
                          <label className="inline-flex items-center gap-1.5 text-xs text-primary cursor-pointer hover:underline">
                            {uploadingCover ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            {uploadingCover ? (language === 'es' ? 'Subiendo...' : 'Uploading...') : (language === 'es' ? 'Subir portada' : 'Upload cover')}
                            <input type="file" accept="image/*" className="hidden" data-testid="agency-cover-upload" onChange={e => uploadAgencyAsset(e.target.files?.[0], 'cover_image')} />
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Tagline + Brand color */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label>{language === 'es' ? 'Lema' : 'Tagline'}</Label>
                        <Input value={agencyForm.tagline} onChange={e => setAgencyForm(f => ({...f, tagline: e.target.value}))} placeholder={language === 'es' ? 'Ej: Casas de confianza' : 'e.g. Homes you can trust'} className="mt-1" data-testid="agency-tagline-input" />
                      </div>
                      <div>
                        <Label>{language === 'es' ? 'Color de Marca' : 'Brand Color'}</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="color" value={agencyForm.brand_color || '#0B192C'} onChange={e => setAgencyForm(f => ({...f, brand_color: e.target.value}))} className="h-9 w-12 rounded border cursor-pointer" data-testid="agency-brand-color-picker" />
                          <Input value={agencyForm.brand_color} onChange={e => setAgencyForm(f => ({...f, brand_color: e.target.value}))} placeholder="#0B192C" className="flex-1" data-testid="agency-brand-color-input" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="submit-agency-btn">
                    {editingAgencyId
                      ? (language === 'es' ? 'Guardar Cambios' : 'Save Changes')
                      : t('admin.createAgency')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {agencies.map(agency => (
              <Card key={agency.id} data-testid={`agency-card-${agency.id}`} className="overflow-hidden">
                {agency.cover_image && (
                  <div className="h-20 w-full bg-slate-100" style={{ backgroundImage: `url(${agency.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                )}
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {agency.logo ? (
                        <img src={agency.logo} alt={agency.name} className="w-12 h-12 rounded-lg object-cover border" data-testid={`agency-logo-${agency.id}`} />
                      ) : (
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: (agency.brand_color || 'rgba(11,25,44,0.08)') + (agency.brand_color ? '20' : '') }}>
                          <Building2 className="w-5 h-5" style={{ color: agency.brand_color || '#0B192C' }} />
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-semibold truncate">{agency.name}</h3>
                        {agency.tagline ? (
                          <p className="text-xs italic text-slate-500 truncate">"{agency.tagline}"</p>
                        ) : (
                          <p className="text-xs text-slate-500 truncate">{agency.email || agency.phone || '-'}</p>
                        )}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="h-8 gap-1 shrink-0" onClick={() => openEditAgency(agency)} data-testid={`agency-edit-${agency.id}`}>
                      <Pencil className="w-3.5 h-3.5" />{language === 'es' ? 'Editar' : 'Edit'}
                    </Button>
                    {currentUser?.role === 'superadmin' && (
                      <Button size="sm" variant="outline" className="h-8 gap-1 shrink-0 border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => deleteAgency(agency)} data-testid={`agency-delete-${agency.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  {agency.description && <p className="text-sm text-slate-600 line-clamp-2">{agency.description}</p>}
                  {agency.address && <p className="text-xs text-slate-500 mt-2">{agency.address}</p>}
                </CardContent>
              </Card>
            ))}
            {agencies.length === 0 && <p className="text-slate-500 col-span-2 text-center py-8">{language === 'es' ? 'No hay agencias aun' : 'No agencies yet'}</p>}
          </div>
          <AdminPagination page={agenciesPaged.page} totalPages={agenciesPaged.totalPages}
            setPage={agenciesPaged.setPage} totalItems={agenciesPaged.filtered.length}
            testIdPrefix="agencies-pagination" />
        </TabsContent>

        {/* ===== USER ANALYTICS TAB ===== */}
        <TabsContent value="analytics">
          <UserAnalyticsTab />
        </TabsContent>

        {/* ===== VERIFICATIONS TAB ===== */}
        <TabsContent value="verifications">
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { val: '', label: language === 'es' ? 'Todos' : 'All' },
              { val: 'documents_uploaded', label: language === 'es' ? 'Subido' : 'Uploaded' },
              { val: 'under_review', label: language === 'es' ? 'En Revisión' : 'Under Review' },
              { val: 'changes_requested', label: language === 'es' ? 'Cambios Sol.' : 'Changes Req.' },
              { val: 'verified', label: language === 'es' ? 'Verificado' : 'Verified' },
              { val: 'rejected', label: language === 'es' ? 'Rechazado' : 'Rejected' },
            ].map(s => (
              <Button key={s.val} variant={verifStatusFilter === s.val ? 'default' : 'outline'} size="sm"
                className={verifStatusFilter === s.val ? 'bg-primary text-white' : ''}
                onClick={() => setVerifStatusFilter(s.val)} data-testid={`verif-filter-${s.val || 'all'}`}>
                {s.label}
              </Button>
            ))}
          </div>
          {verifPaged.filtered.length > 0 ? (
            <div className="space-y-3">
              {verifPaged.paged.map(v => {
                const u = v.user || {};
                const ai = v.ai_result || {};
                const aiStatus = v.ai_status || (ai.face_match ? 'completed' : 'pending');
                const adminStatus = v.admin_status || '';
                const canRunAI = v.status !== 'verified' && v.status !== 'not_started';
                const canAdminReview = (v.status === 'ai_reviewed' || v.status === 'under_review') && v.status !== 'verified';
                const isSuper = currentUser?.role === 'superadmin';
                const issues = isSuper ? computeVerificationDiscrepancies(v, v.user || {}, language) : [];
                const criticalCount = issues.filter(i => i.severity === 'high').length;
                const blockApproval = isSuper && criticalCount > 0;
                const statusColors = {
                  not_started: 'bg-slate-100 text-slate-500',
                  documents_uploaded: 'bg-amber-100 text-amber-700',
                  under_review: 'bg-blue-100 text-blue-700',
                  ai_reviewed: 'bg-indigo-100 text-indigo-700',
                  verified: 'bg-green-100 text-green-700',
                  rejected: 'bg-red-100 text-red-700',
                };
                const aiStatusColors = {
                  pending: 'bg-slate-100 text-slate-500',
                  running: 'bg-blue-100 text-blue-700',
                  passed: 'bg-green-100 text-green-700',
                  failed: 'bg-red-100 text-red-700',
                  needs_review: 'bg-amber-100 text-amber-700',
                };
                return (
                  <Card key={v.id} className={canAdminReview ? 'border-amber-200' : ''} data-testid={`verification-${v.id}`}>
                    <CardContent className="p-4">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            {v.role === 'agent' ? <Building2 className="w-5 h-5 text-primary" /> : <Shield className="w-5 h-5 text-primary" />}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{u.name || u.email}</p>
                            <p className="text-xs text-slate-400">{u.email} - {v.role}</p>
                            {currentUser?.role === 'superadmin' && (
                              <div className="mt-1.5">
                                <VerificationDiscrepancyBadge verification={v} user={u} onResolved={fetchData} />
                              </div>
                            )}
                          </div>
                        </div>
                        <Badge className={statusColors[v.status] || 'bg-slate-100 text-slate-500'}>{v.status?.replace(/_/g, ' ')}</Badge>
                      </div>

                      {/* 2-Step Progress */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className={`rounded-lg p-2.5 border ${aiStatus === 'passed' ? 'border-green-200 bg-green-50' : aiStatus === 'failed' ? 'border-red-200 bg-red-50' : aiStatus === 'running' ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
                          <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">{language === 'es' ? 'Paso 1: Verificacion IA' : 'Step 1: AI Verification'}</p>
                          <Badge className={aiStatusColors[aiStatus] || 'bg-slate-100 text-slate-500'}>
                            {aiStatus === 'running' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                            {aiStatus === 'passed' ? (language === 'es' ? 'Aprobado' : 'Passed') :
                             aiStatus === 'failed' ? (language === 'es' ? 'Fallido' : 'Failed') :
                             aiStatus === 'needs_review' ? (language === 'es' ? 'Revision Manual' : 'Needs Review') :
                             aiStatus === 'running' ? (language === 'es' ? 'Ejecutando...' : 'Running...') :
                             (language === 'es' ? 'Pendiente' : 'Pending')}
                          </Badge>
                        </div>
                        <div className={`rounded-lg p-2.5 border ${adminStatus === 'approved' ? 'border-green-200 bg-green-50' : adminStatus === 'rejected' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                          <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">{language === 'es' ? 'Paso 2: Revision Admin' : 'Step 2: Admin Review'}</p>
                          <Badge className={adminStatus === 'approved' ? 'bg-green-100 text-green-700' : adminStatus === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}>
                            {adminStatus === 'approved' ? (language === 'es' ? 'Aprobado' : 'Approved') :
                             adminStatus === 'rejected' ? (language === 'es' ? 'Rechazado' : 'Rejected') :
                             (language === 'es' ? 'Pendiente' : 'Pending')}
                          </Badge>
                        </div>
                      </div>

                      {/* Documents list */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                        {Object.entries(v.documents || {}).map(([docType, docData]) => (
                          <div key={docType} className="text-xs bg-slate-50 rounded-lg p-2">
                            <p className="font-medium capitalize">{docType.replace(/_/g, ' ')}</p>
                            <p className="text-slate-400 truncate">{docData.file_name || 'uploaded'}</p>
                            {docData.url && docData.url !== '' && (
                              <a href={docData.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${docData.url}` : docData.url} target="_blank" rel="noreferrer" className="text-[#D4AF37] underline text-[10px]">
                                {language === 'es' ? 'Ver' : 'View'}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* AI Result Details */}
                      {(ai.face_match || ai.cedula_name || ai.error) && (
                        <div className={`rounded-lg p-3 mb-3 text-xs border ${aiStatus === 'passed' ? 'bg-green-50 border-green-200' : aiStatus === 'failed' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`} data-testid="ai-result-detail">
                          <p className="font-semibold text-slate-600 mb-2">{language === 'es' ? 'Resultado Verificacion IA' : 'AI Verification Result'}</p>
                          <div className="space-y-2">
                            {/* Face Match */}
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500 w-28">{language === 'es' ? 'Coincidencia Facial' : 'Face Match'}:</span>
                              <strong className={ai.face_match === 'match' ? 'text-green-600' : ai.face_match === 'no_match' ? 'text-red-600' : 'text-amber-600'}>
                                {ai.face_match === 'match' ? (language === 'es' ? 'Coincide' : 'Match') : ai.face_match === 'no_match' ? (language === 'es' ? 'No Coincide' : 'No Match') : (language === 'es' ? 'Incierto' : 'Uncertain')}
                              </strong>
                            </div>
                            {/* Cedula Name */}
                            {ai.cedula_name && (
                              <div className="flex items-center gap-2">
                                <span className="text-slate-500 w-28">{language === 'es' ? 'Nombre Cedula' : 'Cedula Name'}:</span>
                                <strong>{ai.cedula_name}</strong>
                              </div>
                            )}
                            {/* Cedula Number */}
                            {ai.cedula_number && (
                              <div className="flex items-center gap-2">
                                <span className="text-slate-500 w-28">{language === 'es' ? 'Numero Cedula' : 'Cedula #'}:</span>
                                <strong>{ai.cedula_number}</strong>
                              </div>
                            )}
                            {/* Name Cross-Check */}
                            {ai.name_matches && ai.name_matches.length > 0 && (
                              <div>
                                <p className="text-slate-500 font-semibold mt-1 mb-1">{language === 'es' ? 'Verificacion de Nombre entre Documentos' : 'Name Cross-Check Across Documents'}:</p>
                                <div className="space-y-1 ml-2">
                                  {ai.name_matches.map((nm, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                      {nm.matches_cedula ? <CheckCircle className="w-3 h-3 text-green-600" /> : <XCircle className="w-3 h-3 text-red-600" />}
                                      <span className="capitalize">{nm.document}:</span>
                                      <span className="text-slate-600">"{nm.name_found}"</span>
                                      <span className={nm.matches_cedula ? 'text-green-600' : 'text-red-600'}>
                                        {nm.matches_cedula ? (language === 'es' ? 'Coincide' : 'Matches') : (language === 'es' ? 'No Coincide' : 'Mismatch')}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-1.5 flex items-center gap-1">
                                  <span className="text-slate-500">{language === 'es' ? 'Nombres Consistentes' : 'Names Consistent'}:</span>
                                  <strong className={ai.names_consistent ? 'text-green-600' : 'text-red-600'}>
                                    {ai.names_consistent ? (language === 'es' ? 'Si' : 'Yes') : 'No'}
                                  </strong>
                                </div>
                              </div>
                            )}
                            {/* Confidence */}
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500 w-28">{language === 'es' ? 'Confianza' : 'Confidence'}:</span>
                              <strong className={ai.confidence === 'high' ? 'text-green-600' : ai.confidence === 'low' ? 'text-red-600' : 'text-amber-600'}>{ai.confidence || '-'}</strong>
                            </div>
                            {/* Notes */}
                            {ai.notes && <p className="text-slate-400 mt-1 italic">{ai.notes}</p>}
                            {ai.error && <p className="text-red-500 mt-1">{ai.error}</p>}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {currentUser?.role === 'superadmin' && (
                        <div className="mb-3" data-testid={`vdata-block-${v.user_id}`}>
                          <VerificationDataCard mode="admin" targetUserId={v.user_id} refreshKey={v.ai_extracted_at || v.updated_at || 0} />
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {/* Run AI Button */}
                        {canRunAI && (
                          <Button size="sm" variant="outline" className="gap-1" data-testid={`run-ai-${v.id}`}
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/verification/admin/${v.id}/run-ai`, {}, { headers });
                                toast.success(language === 'es' ? 'Verificacion IA iniciada — actualizando...' : 'AI verification started — refreshing...');
                                // Poll fetchData every 3s for up to 30s while the background AI run completes
                                const startIso = v.ai_extracted_at || v.updated_at || '';
                                let polls = 0;
                                const poll = async () => {
                                  polls += 1;
                                  await fetchData();
                                  // Stop early if a re-render isn't immediately reflected — we'll just keep polling for 10x3s anyway
                                  if (polls < 10) {
                                    setTimeout(poll, 3000);
                                  }
                                };
                                setTimeout(poll, 3000);
                              } catch { toast.error('Failed'); }
                            }}>
                            <Play className="w-3.5 h-3.5" />{language === 'es' ? 'Ejecutar IA' : 'Run AI Check'}
                          </Button>
                        )}
                        {/* Admin Approve/Reject */}
                        {canAdminReview && (
                          <>
                            <Button size="sm"
                              disabled={blockApproval}
                              title={blockApproval
                                ? (language === 'es' ? 'Resuelve las discrepancias críticas antes de aprobar' : 'Resolve critical discrepancies before approving')
                                : ''}
                              className="bg-green-600 hover:bg-green-700 text-white gap-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-400"
                              data-testid={`approve-verif-${v.id}`}
                              onClick={async () => {
                                if (blockApproval) {
                                  toast.error(language === 'es'
                                    ? `Aprobación bloqueada: ${criticalCount} discrepancia(s) crítica(s) sin resolver`
                                    : `Approval blocked: ${criticalCount} unresolved critical discrepancy(ies)`);
                                  return;
                                }
                                try {
                                  await axios.put(`${API}/verification/admin/${v.id}/review`, { action: 'approve', notes: '' }, { headers });
                                  toast.success(language === 'es' ? 'Verificacion aprobada' : 'Verification approved');
                                  fetchData();
                                } catch { toast.error('Failed'); }
                              }}>
                              <CheckCircle className="w-3.5 h-3.5" />{language === 'es' ? 'Aprobar' : 'Approve'}
                            </Button>
                            {blockApproval && (
                              <p className="basis-full text-[11px] text-red-600 flex items-center gap-1.5 mt-1" data-testid={`approval-blocked-${v.id}`}>
                                <AlertTriangle className="w-3 h-3" />
                                {language === 'es'
                                  ? `Aprobación bloqueada — resuelve ${criticalCount} discrepancia(s) crítica(s) primero`
                                  : `Approval blocked — resolve ${criticalCount} critical discrepancy(ies) first`}
                              </p>
                            )}
                            <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-200" data-testid={`reject-verif-${v.id}`}
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/verification/admin/${v.id}/review`, { action: 'reject', notes: '' }, { headers });
                                  toast.success(language === 'es' ? 'Verificacion rechazada' : 'Verification rejected');
                                  fetchData();
                                } catch { toast.error('Failed'); }
                              }}>
                              <XCircle className="w-3.5 h-3.5" />{language === 'es' ? 'Rechazar' : 'Reject'}
                            </Button>
                          </>
                        )}
                      </div>

                      {v.status === 'verified' && (
                        <div className="flex items-center gap-2 text-xs text-green-600 mt-2">
                          <VerifiedBadge role={v.role} size="xs" />
                          <span>{language === 'es' ? 'Verificado' : 'Verified'} - {language === 'es' ? 'Expira' : 'Expires'}: {v.expires_at ? new Date(v.expires_at).toLocaleDateString() : '-'}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-border">
              <BadgeCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{language === 'es' ? 'No hay solicitudes de verificacion' : 'No verification requests'}</p>
            </div>
          )}
          <AdminPagination page={verifPaged.page} totalPages={verifPaged.totalPages}
            setPage={verifPaged.setPage} totalItems={verifPaged.filtered.length}
            testIdPrefix="verif-pagination" />
        </TabsContent>

        {/* ===== PROPERTY VERIFICATIONS TAB ===== */}
        <TabsContent value="property-verifications">
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { val: '', label: language === 'es' ? 'Todos' : 'All' },
              { val: 'not_started', label: language === 'es' ? 'No Iniciado' : 'Not Started' },
              { val: 'documents_uploaded', label: language === 'es' ? 'Subido' : 'Uploaded' },
              { val: 'under_review', label: language === 'es' ? 'En Revisión' : 'Under Review' },
              { val: 'verified', label: language === 'es' ? 'Verificada' : 'Verified' },
              { val: 'rejected', label: language === 'es' ? 'Rechazada' : 'Rejected' },
            ].map(s => (
              <Button key={s.val} variant={pvStatusFilter === s.val ? 'default' : 'outline'} size="sm"
                className={pvStatusFilter === s.val ? 'bg-primary text-white' : ''}
                onClick={() => setPvStatusFilter(s.val)} data-testid={`pv-filter-${s.val || 'all'}`}>
                {s.label}
              </Button>
            ))}
          </div>
          {pvPaged.filtered.length > 0 ? (
            <div className="space-y-3" data-testid="property-verifications-list">
              {pvPaged.paged.map(pv => (
                <PropertyVerificationCard key={pv.id} pv={pv} headers={headers} onChange={fetchData} language={language} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-border">
              <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{language === 'es' ? 'No hay verificaciones de propiedades pendientes' : 'No property verification requests'}</p>
            </div>
          )}
          <AdminPagination page={pvPaged.page} totalPages={pvPaged.totalPages}
            setPage={pvPaged.setPage} totalItems={pvPaged.filtered.length}
            testIdPrefix="pv-pagination" />
        </TabsContent>

        {/* ===== STATES IMAGES TAB ===== */}
        <TabsContent value="states">
          <StatesAdminTab />
        </TabsContent>

        {/* ===== AUDIT LOG TAB (superadmin only) ===== */}
        {currentUser?.role === 'superadmin' && (
          <>
          <TabsContent value="beta">
            <BetaAdminTab headers={headers} language={language} />
          </TabsContent>

          <TabsContent value="service-agreement">
            <ServiceAgreementAdminTab headers={headers} language={language} />
          </TabsContent>

          <TabsContent value="feedback">
            <FeedbackAdminTab headers={headers} language={language} />
          </TabsContent>

          <TabsContent value="audit-log">
            <AuditLogTab />
          </TabsContent>
          </>
        )}
      </Tabs>

      {/* ===== Super Admin password reset dialog ===== */}
      <Dialog open={!!pwResetUser} onOpenChange={(o) => { if (!o) { setPwResetUser(null); setPwResetValue(''); } }}>
        <DialogContent className="max-w-md" data-testid="pw-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-600" />
              {language === 'es' ? 'Restablecer Contraseña' : 'Reset Password'}
            </DialogTitle>
          </DialogHeader>
          {pwResetUser && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-amber-900">{pwResetUser.name} <span className="text-slate-500 font-normal">· {pwResetUser.email}</span></p>
                <p className="text-xs text-amber-800 mt-1">
                  {language === 'es' ? 'Agencia' : 'Agency'}: {agencies.find(a => a.id === pwResetUser.agency_id)?.name || pwResetUser.agency_id}
                </p>
              </div>
              <div>
                <Label>{language === 'es' ? 'Nueva contraseña' : 'New password'}</Label>
                <Input
                  type="password"
                  value={pwResetValue}
                  onChange={(e) => setPwResetValue(e.target.value)}
                  placeholder={language === 'es' ? 'Mínimo 6 caracteres' : 'Min 6 characters'}
                  className="mt-1"
                  data-testid="pw-reset-new-input"
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1.5">
                  {language === 'es'
                    ? 'El usuario recibirá un correo confirmando el cambio. La acción se registra en el log de auditoría.'
                    : "The user will receive a confirmation email. This action is recorded in the audit log."}
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setPwResetUser(null); setPwResetValue(''); }} data-testid="pw-reset-cancel">
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button
                  onClick={submitPasswordReset}
                  disabled={pwResetSubmitting || !pwResetValue || pwResetValue.length < 6}
                  className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                  data-testid="pw-reset-submit"
                >
                  {pwResetSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  {language === 'es' ? 'Restablecer' : 'Reset Password'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Super Admin per-user listing-limit override dialog ===== */}
      <Dialog open={!!capUser} onOpenChange={(o) => { if (!o) { setCapUser(null); setCapDetail(null); } }}>
        <DialogContent className="max-w-lg" data-testid="cap-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gauge className="w-5 h-5 text-[#D4AF37]" />
              {language === 'es' ? 'Límite de Propiedades' : 'Listing Limit Override'}
            </DialogTitle>
          </DialogHeader>
          {capUser && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-[#0B192C]">
                  {capUser.name} <span className="text-slate-500 font-normal">· {capUser.email}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {capUser.role} {capUser.agent_tier && `· ${capUser.agent_tier}`}
                </p>
              </div>
              {capLoading ? (
                <div className="py-6 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : capDetail && (
                <>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
                      <div className="text-slate-500">{language === 'es' ? 'Plan' : 'Plan'}</div>
                      <div className="text-base font-semibold text-[#0B192C]" data-testid="cap-plan-limit">
                        {capDetail.plan_limit == null ? '∞' : capDetail.plan_limit}
                      </div>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
                      <div className="text-slate-500">{language === 'es' ? 'Activas' : 'Active'}</div>
                      <div className="text-base font-semibold text-[#0B192C]" data-testid="cap-active-count">{capDetail.active_count}</div>
                    </div>
                    <div className={`border rounded-md p-2 ${capDetail.has_override ? 'bg-amber-50 border-[#D4AF37]' : 'bg-slate-50 border-slate-200'}`}>
                      <div className="text-slate-500">{language === 'es' ? 'Efectivo' : 'Effective'}</div>
                      <div className="text-base font-semibold text-[#0B192C]" data-testid="cap-effective-limit">
                        {capDetail.effective_limit == null ? '∞' : capDetail.effective_limit}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      {language === 'es' ? 'Nuevo límite' : 'Override value'}
                      <span className="text-[10px] text-slate-400 font-normal">
                        {language === 'es' ? 'Vacío = quitar override' : 'Empty = remove override'}
                      </span>
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={capValue}
                      onChange={(e) => setCapValue(e.target.value)}
                      disabled={capUnlimited}
                      placeholder={language === 'es' ? 'Ej. 25' : 'e.g. 25'}
                      className="mt-1"
                      data-testid="cap-value-input"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      {language === 'es'
                        ? '0 = bloquea nuevas propiedades (los listados actuales permanecen).'
                        : '0 = block new listings (existing ones stay live).'}
                    </p>
                  </div>

                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={capUnlimited}
                      onChange={(e) => setCapUnlimited(e.target.checked)}
                      data-testid="cap-unlimited-checkbox"
                    />
                    <InfinityIcon className="w-3.5 h-3.5 text-[#D4AF37]" />
                    {language === 'es' ? 'Otorgar ilimitado' : 'Grant unlimited'}
                  </label>

                  <div>
                    <Label>{language === 'es' ? 'Motivo / nota' : 'Reason / note'}</Label>
                    <Input
                      value={capNote}
                      onChange={(e) => setCapNote(e.target.value)}
                      placeholder={language === 'es' ? 'Ej. Deal empresarial Q1 2026' : 'e.g. Enterprise deal Q1 2026'}
                      className="mt-1"
                      maxLength={500}
                      data-testid="cap-note-input"
                    />
                  </div>

                  {capDetail.audit_history && capDetail.audit_history.length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-500 flex items-center gap-1">
                        <History className="w-3 h-3" />
                        {language === 'es' ? 'Historial' : 'Audit history'} ({capDetail.audit_history.length})
                      </summary>
                      <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto border border-slate-100 rounded p-2 bg-slate-50">
                        {capDetail.audit_history.map((a, i) => (
                          <li key={a.id || i} className="text-[10px] text-slate-600">
                            <span className="text-slate-400">{a.at.slice(0, 19).replace('T', ' ')}</span>
                            {' · '}
                            <span className="font-medium">{String(a.previous ?? 'none')}</span>
                            {' → '}
                            <span className="font-medium">{String(a.new_value ?? 'none')}</span>
                            {' · by '}
                            <span>{a.actor_email}</span>
                            {a.note && <div className="text-slate-500 italic">"{a.note}"</div>}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  <div className="flex flex-wrap gap-2 justify-end pt-2">
                    <Button variant="outline" onClick={() => setCapUser(null)} data-testid="cap-cancel-btn">
                      {language === 'es' ? 'Cancelar' : 'Cancel'}
                    </Button>
                    {capDetail.has_override && (
                      <Button
                        variant="outline"
                        onClick={() => submitCap('clear')}
                        disabled={capSubmitting}
                        className="border-red-300 text-red-700 hover:bg-red-50 gap-1.5"
                        data-testid="cap-clear-btn"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {language === 'es' ? 'Quitar override' : 'Clear override'}
                      </Button>
                    )}
                    <Button
                      onClick={() => submitCap('save')}
                      disabled={capSubmitting}
                      className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold gap-1.5"
                      data-testid="cap-save-btn"
                    >
                      {capSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gauge className="w-4 h-4" />}
                      {language === 'es' ? 'Guardar override' : 'Save override'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PropertyVerificationCard({ pv, headers, onChange, language }) {
  const [running, setRunning] = useState('');
  const [acting, setActing] = useState(false);
  const [notes, setNotes] = useState(pv.admin_notes || '');
  const tt = (en, es) => (language === 'es' ? es : en);

  const runAi = async (docType) => {
    setRunning(docType);
    try {
      await axios.post(`${API}/property-verification/admin/${pv.id}/run-ai?doc_type=${docType}`, {}, { headers });
      toast.success(tt(`AI check started for ${docType}`, `IA iniciada para ${docType}`));
      setTimeout(onChange, 1500);
    } catch { toast.error(tt('Failed to run AI', 'Error al iniciar IA')); }
    finally { setRunning(''); }
  };

  const review = async (action) => {
    setActing(true);
    try {
      await axios.put(`${API}/property-verification/admin/${pv.id}/review`, { action, notes }, { headers });
      toast.success(tt(`Property ${action}d`, `Propiedad ${action === 'approve' ? 'aprobada' : 'rechazada'}`));
      onChange();
    } catch { toast.error(tt('Action failed', 'Error')); }
    finally { setActing(false); }
  };

  const docs = pv.documents || {};
  const status = pv.status;
  return (
    <Card data-testid={`pv-card-${pv.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-semibold text-base text-[#0B192C]">{pv.property?.title || 'Unknown property'}</h3>
              <Badge className={status === 'verified' ? 'bg-green-100 text-green-700' : status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                {status.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {tt('Owner', 'Propietario')}: {pv.owner?.name} ({pv.owner?.email})
              {pv.property?.city && ` · ${pv.property.city}`}
            </p>
          </div>
          <Link to={`/property/${pv.property_id}`} target="_blank" className="text-xs text-[#D4AF37] hover:underline">{tt('View property', 'Ver propiedad')} →</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(docs).map(([key, d]) => (
            <div key={key} className="bg-slate-50 rounded-lg p-2 text-xs" data-testid={`pv-doc-${pv.id}-${key}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold capitalize truncate">{key.replace(/_/g, ' ')}</span>
                <span className="flex items-center gap-1">
                  {d.ai_status === 'running' && <Loader2 className="w-3 h-3 animate-spin text-blue-600" />}
                  {d.ai_status === 'complete' && d.ai_result?.valid && <Check className="w-3 h-3 text-green-600" />}
                  {d.ai_status === 'complete' && d.ai_result && d.ai_result.valid === false && <XCircle className="w-3 h-3 text-red-600" />}
                  {d.ai_status === 'failed' && <AlertTriangle className="w-3 h-3 text-amber-600" />}
                  <Button
                    onClick={() => runAi(key)}
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-[10px]"
                    disabled={running === key || ['verified', 'rejected'].includes(status)}
                    data-testid={`run-ai-${pv.id}-${key}`}
                  >
                    <Play className="w-2.5 h-2.5 mr-0.5" />{running === key ? '…' : 'AI'}
                  </Button>
                </span>
              </div>
              {d.file_name && <p className="text-[10px] text-slate-400 truncate mt-0.5">{d.file_name}</p>}
              {d.ai_result?.summary && <p className="text-[10px] text-slate-600 italic mt-0.5">"{d.ai_result.summary}"</p>}
              {d.ai_result?.concerns && <p className="text-[10px] text-amber-700 mt-0.5">⚠ {d.ai_result.concerns}</p>}
            </div>
          ))}
        </div>

        <textarea
          className="w-full text-xs p-2 border border-slate-200 rounded-md disabled:bg-slate-50 disabled:text-slate-400"
          rows={2}
          placeholder={tt('Admin notes (optional)', 'Notas del admin (opcional)')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={['verified', 'rejected'].includes(status)}
          data-testid={`pv-notes-${pv.id}`}
        />
        <div className="flex gap-2 justify-end">
          <Button
            onClick={() => review('reject')}
            disabled={acting || ['verified', 'rejected'].includes(status)}
            size="sm"
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 gap-1"
            data-testid={`pv-reject-${pv.id}`}
          >
            <XCircle className="w-3.5 h-3.5" />{tt('Reject', 'Rechazar')}
          </Button>
          <Button
            onClick={() => review('approve')}
            disabled={acting || ['verified', 'rejected'].includes(status)}
            size="sm"
            className="bg-green-600 hover:bg-green-700 text-white gap-1 disabled:bg-slate-300"
            data-testid={`pv-approve-${pv.id}`}
          >
            <CheckCircle className="w-3.5 h-3.5" />{tt('Approve', 'Aprobar')}
          </Button>
        </div>
        {pv.admin_notes && status === 'rejected' && (
          <p className="text-xs text-red-700 bg-red-50 p-2 rounded-md">{pv.admin_notes}</p>
        )}
      </CardContent>
    </Card>
  );
}
